import { Coordinate } from '@shared/models/geography.model';
import { PvgisWeatherHour } from '@shared/models/pvgis-weather.model';
import { DateTime } from 'luxon';
import { clamp } from '../math.helpers';
import { calculateSolarPosition } from '../astronomy.helpers';
import { HOURS_PER_YEAR } from '@shared/constants/math.constants';
import {
  EnvironmentHourData,
  InsolationSurface,
  OrientedInsolationSurface,
} from './weather.models';
import { guards } from '../typeguards.helpers';

/**
 * Calculate ground albedo factor based on season, temperature, and latitude
 * @param dayIndex Day of the year (0-364)
 * @param meanTemp Daily mean temperature in Celsius
 * @param coords Geographic coordinates
 * @returns Albedo factor (0-1) - fraction of solar radiation reflected by ground surface
 */
export const calculateAlbedo = (
  dayIndex: number,
  meanTemp: number,
  coords: Coordinate,
): number => {
  const lat = coords.lat;
  const absLat = Math.abs(lat);
  const trop = absLat < 15;

  // Seasonal phase (southern hemisphere inverted)
  const phase =
    Math.sin((2 * Math.PI * (dayIndex + 10)) / 365) * (lat < 0 ? -1 : 1);

  // Base albedo values by latitude
  const aSummer = trop ? 0.18 : absLat < 40 ? 0.2 : 0.22;
  const aWinter = trop ? 0.22 : absLat < 40 ? 0.28 : 0.32;
  // Map seasonal phase so that winter (phase ~ -1 in north, +1 in south) yields higher albedo than summer
  // At phase = -1 => base = aWinter; at phase = +1 => base = aSummer (north). Phase is inverted for south
  const base = aSummer + ((aWinter - aSummer) * (phase + 1)) / 2;

  // Snow factor based on temperature
  let snowFactor = 0;
  if (meanTemp < 1) snowFactor = meanTemp < -5 ? 1 : 1 - (meanTemp + 5) / 6;

  // Maximum snow albedo by latitude
  let maxSnow = 0.5; // absLat <= 30
  if (absLat > 60) maxSnow = 0.7;
  else if (absLat > 50) maxSnow = 0.65;
  else if (absLat > 40) maxSnow = 0.6;
  else if (absLat > 30) maxSnow = 0.55;

  const withSnow = base + snowFactor * (maxSnow - base);
  const damp = trop ? 0.4 : 1;

  const albedo = base * (1 - 0.5 * (1 - damp)) + (withSnow - base) * damp;

  return clamp(albedo, 0, 1);
};

/**
 * Populates each weather hour with:
 *  - solarElevationRad (radians)
 *  - solarAzimuthDeg (degrees, 0=N, 90=E)
 *  - dayIndex (0..364)
 *  - albedo (dynamic heuristic based on season, temp & latitude)
 *
 * Assumes exactly 8760 hours (365 days). Leap years must be normalized beforehand.
 */
export function enrichWeatherHours(
  weatherHours: PvgisWeatherHour[],
  coords: Coordinate,
): EnvironmentHourData[] {
  if (!Array.isArray(weatherHours) || weatherHours.length !== HOURS_PER_YEAR) {
    throw new Error(
      `Expected ${HOURS_PER_YEAR} hours (365 days). Got ${weatherHours.length}. Remove Feb 29 if present.`,
    );
  }

  // --- Daily mean temperature (for albedo heuristic) ---
  const dailyMeanTemp = weatherHours
    .reduce<number[]>((acc, data, hourIndex) => {
      const dayIndex = Math.floor(hourIndex / 24);
      acc[dayIndex] = (acc[dayIndex] ?? 0) + data.T2m;
      return acc;
    }, [])
    .map((dayTotal) => dayTotal / 24);

  const baseDate = DateTime.fromObject({ year: 2030 }, { zone: 'UTC' }); // fixed non-leap year

  // --- Populate each hour ---
  return weatherHours.map<EnvironmentHourData>((weatherHour, hourIndex) => {
    const dayIndex = Math.floor(hourIndex / 24);
    const date = baseDate.plus({ hours: hourIndex }).toJSDate();

    // *** Solar position ***
    const { elevationRad, azimuthDeg } = calculateSolarPosition(date, coords);

    // *** Dynamic albedo heuristic ***
    const meanT = dailyMeanTemp[dayIndex] ?? NaN;

    const albedo = calculateAlbedo(dayIndex, meanT, coords);

    // *** Store results ***
    return {
      ...weatherHour,
      solarElevationRad: elevationRad,
      solarAzimuthDeg: azimuthDeg,
      albedo: albedo,
      dayIndex: dayIndex,
    };
  });
}

export const deg2rad = (d: number) => (d * Math.PI) / 180;

/** How diffuse irradiance is transposed onto a tilted surface */
export type DiffuseModel =
  | 'perez' // Perez model, more accurate
  | 'isotropic'; // Faster, isotropic (100% overcast simplification)

/**
 * Calculates solar power in W during the supplied hour on the supplied surfaces
 */
export const getHourSurfaceInsolationInW = (
  hour: EnvironmentHourData, // the hour to calculate
  surfaces: OrientedInsolationSurface[], // oriented surfaces (direction & tilt)
  diffuseModel: DiffuseModel,
): number => {
  const Gb_n = hour['Gb(n)'];
  const Gd_h = hour['Gd(h)'];
  const G_h = hour['G(h)'];
  if (Gb_n <= 0 && Gd_h <= 0 && G_h <= 0) return 0;

  // Solar position
  const elevRad = hour.solarElevationRad;
  if (elevRad <= 0) return 0;
  const azRad = (hour.solarAzimuthDeg * Math.PI) / 180;
  const cosZ = Math.max(0, Math.sin(elevRad)); // sin(elev) = cos(zenith)
  const sE = Math.cos(elevRad) * Math.sin(azRad);
  const sN = Math.cos(elevRad) * Math.cos(azRad);
  const sU = Math.sin(elevRad);

  // Ground reflection
  const alb = clamp(hour.albedo, 0, 1) ?? 0.2; // default to 0.2 if not set

  let w = 0;
  // Loop all surfaces/windows
  for (const surface of surfaces) {
    const betaDeg = 90 - surface.tilt; // tilt=0 (vertical) => beta=90
    const betaRad = clamp((betaDeg * Math.PI) / 180, 0, Math.PI); // clamp

    // Calc cos Incidence
    const surfAzRad = deg2rad(surface.direction);
    const nE = Math.sin(betaRad) * Math.sin(surfAzRad);
    const nN = Math.sin(betaRad) * Math.cos(surfAzRad);
    const nU = Math.cos(betaRad);
    const cosInc = sE * nE + sN * nN + sU * nU;

    // Beams
    const beam = Math.max(0, Gb_n * cosInc);
    // Diffuse
    const diffuse =
      diffuseModel === 'perez'
        ? computePerezDiffusePOA(
            Gd_h,
            Gb_n,
            G_h,
            cosInc,
            cosZ,
            betaRad,
            hour.dayIndex,
          )
        : (Gd_h * (1 + Math.cos(betaRad))) / 2;
    // Ground reflection
    const refl = (alb * G_h * (1 - Math.cos(betaRad))) / 2;
    // Total
    w += Math.max(0, beam + diffuse + refl) * surface.area * surface.factor;
  }

  return w;
};

/** Perez epsilon bin upper bounds (clearness classes) */
const PEREZ_EPS_BINS = [1.065, 1.23, 1.5, 1.95, 2.8, 4.5, 6.2, Infinity];

/**
 * Perez coefficients (a,b,c,d,e,f) per epsilon bin.
 * Standard (Perez 1990); används i många implementationer (pvlib m.fl.)
 */
const PEREZ_COEFFICIENTS = {
  a: [-0.008, 0.13, 0.33, 0.568, 0.873, 1.132, 1.06, 0.678],
  b: [0.588, 0.683, 0.487, 0.187, -0.392, -1.237, -1.6, -0.327],
  c: [-0.062, -0.151, -0.221, -0.295, -0.362, -0.412, -0.359, -0.25],
  d: [-0.06, -0.019, 0.055, 0.15, 0.28, 0.43, 0.5, 0.37],
  e: [0.072, 0.066, -0.064, -0.151, -0.28, -0.362, -0.359, -0.25],
  f: [-0.022, -0.029, -0.026, -0.023, -0.018, -0.014, -0.01, -0.005],
} as const;

/**
 * Perez diffuse transposition.
 *
 * @param Gd_h   Diffuse horizontal irradiance (DHI) [W/m2]
 * @param Gb_n   Beam normal irradiance (DNI) [W/m2]
 * @param G_h     Global horizontal irradiance (GHI) [W/m2] (behövs för datakontroll men ej direkt i formeln)
 * @param cosInc cos(θ_i)  mellan solriktning och ytnormal (>=0 om solen "träffar" ytan)
 * @param cosZ   cos(θ_z)  (>=0 när solen över horisonten)
 * @param betaRad Lutningsvinkel från horisontalplanet (rad) (0 = horisontell uppåt, π/2 ≈ vertikal)
 * @param dayIndex 0..364 (0 = 1 jan) – används för extraterrestriell strålning
 * @returns Diffus plane-of-array irradians (W/m2)
 */
export function computePerezDiffusePOA(
  Gd_h: number,
  Gb_n: number,
  G_h: number,
  cosInc: number,
  cosZ: number,
  betaRad: number,
  dayIndex: number,
): number {
  if (Gd_h <= 0 || cosZ <= 0) {
    // Ingen diffus energi projiceras om solen under horisonten (cosZ <=0) eller DHI 0
    return 0;
  }

  // --- 1. Extraterrestriell normal irradians (G_on) ---
  // n = dagnummer 1..365
  const n = dayIndex + 1;
  const G_sc = 1367; // W/m2 (solarkonstant)
  const G_on = G_sc * (1 + 0.033 * Math.cos((2 * Math.PI * n) / 365));

  // --- 2. Solens zenitvinkel (rad) och luftmassa ---
  const thetaZRad = Math.acos(Math.min(Math.max(cosZ, -1), 1));
  const thetaZDeg = (thetaZRad * 180) / Math.PI;

  // Optisk luftmassa (Kasten & Young 1989)
  const m = 1 / (cosZ + 0.50572 * Math.pow(96.07995 - thetaZDeg, -1.6364));

  // --- 3. Sky brightness Δ och clearness ε ---
  // Δ = (DHI * m)/G_on
  const Delta = (Gd_h * m) / Math.max(G_on, 1e-6);

  // ε (Perez)
  const eps =
    ((Gb_n + Gd_h) / Math.max(Gd_h, 1e-6) + 1.041 * Math.pow(thetaZRad, 3)) /
    (1 + 1.041 * Math.pow(thetaZRad, 3));

  // --- 4. Hitta epsilon-klass (bin) ---
  let idx = 0;
  while (idx < PEREZ_EPS_BINS.length && eps > PEREZ_EPS_BINS[idx]!) idx++;
  if (idx >= 8) idx = 7; // skydd

  // --- 5. Perez koefficienter (a,b,c,d,e,f) per bin, se {@link PEREZ_COEFFICIENTS} ---
  const { a, b, c, d, e, f } = PEREZ_COEFFICIENTS;

  // Notation: F1 = a + b*Δ + c*θ_z  ; F2 = d + e*Δ + f*θ_z   (θ_z i rad i denna version)
  // (Vissa källor använder grader för θ_z i regressionsdelen; koefficienterna ovan förutsätter RADIANER – konsekvent med pvlib.)
  // Vill du använda grader, måste du justera koefficienter. Här använder vi radianer.
  const F1raw = a[idx]! + b[idx]! * Delta + c[idx]! * thetaZRad;
  const F2raw = d[idx]! + e[idx]! * Delta + f[idx]! * thetaZRad;

  // F1 kan ej vara negativ
  const F1 = Math.max(0, F1raw);
  // F2 kan bli lätt negativ; klampa lätt (valfritt)
  const F2 = Math.max(-0.5, F2raw);

  // --- 6. Perez formel ---
  // Sätt cosInc/cosZ = Rb men skydda mot /0
  const Rb = cosInc > 0 && cosZ > 1e-6 ? cosInc / cosZ : 0;

  const diffusePOA =
    Gd_h *
    (((1 - F1) * (1 + Math.cos(betaRad))) / 2 +
      F1 * Rb +
      F2 * Math.sin(betaRad));

  // Säkerställ ej negativt
  return diffusePOA > 0 ? diffusePOA : 0;
}

/**
 * Apply missing direction and split surfaces with missing direction into N/E/S/W, 25% each
 * @param surfaces
 * @returns
 */
export const toOrientedInsolationSurface = (
  surfaces: InsolationSurface[],
): OrientedInsolationSurface[] => {
  const orientedSurfaces = surfaces.reduce((acc, s) => {
    const tilt = s.tilt ?? 0;
    const factor = s.factor ?? 1;
    const direction = s.direction;
    if (guards.is.number(direction) && guards.is.finiteNumber(direction)) {
      acc.push({ ...s, direction, tilt, factor });
    }
    // If no direction, split into 4 surfaces
    else {
      const dirs = [0, 90, 180, 270];
      const area = s.area / dirs.length;
      for (const direction of dirs) {
        acc.push({ ...s, area, direction, tilt, factor });
      }
    }
    return acc;
  }, [] as OrientedInsolationSurface[]);

  return mergeSurfaces(orientedSurfaces);
};

/**
 * Merge surfaces with equal direction and tilt into one surface with combined area
 * @param surfaces
 */
const mergeSurfaces = (
  surfaces: OrientedInsolationSurface[],
): OrientedInsolationSurface[] => {
  //  direction|tilt|factor
  type MergeKey = `${number}|${number}|${number}`;
  const merged = surfaces.reduce(
    (acc, s) => {
      const key: MergeKey = `${s.direction}|${s.tilt}|${s.factor}`;
      acc[key] = (acc[key] ?? 0) + s.area;
      return acc;
    },
    {} as Record<string, number>,
  );

  return Object.entries(merged).map(([key, area]) => {
    const [direction, tilt, factor] = key.split('|');
    return {
      area,
      direction: Number(direction),
      tilt: Number(tilt),
      factor: Number(factor),
    };
  });
};
